import { useMemo } from 'react';
import { Button, DatePicker, Label, Select, ToggleGroup } from '@shared/components';
import type { PeriodHeaderModel } from '../../../hooks/useFundStatementPeriodSection';
import type { StatementPeriodMode } from '../../../hooks/useStatementPeriod';
import { isoDateFromPickerDate, pickerDateFromIsoDate } from '../../../utils/statementPeriod';

const MODE_OPTIONS: { value: StatementPeriodMode; label: string }[] = [
    { value: 'month', label: 'Calendar month' },
    { value: 'range', label: 'Date range' },
];

const MONTH_LABEL_ID = 'fund-statement-month-label';
const FROM_ID = 'fund-statement-range-from';
const TO_ID = 'fund-statement-range-to';
const RANGE_ERROR_ID = 'fund-statement-range-error';

export function PeriodHeader({
    mode,
    canChooseRange,
    month,
    monthOptions,
    rangeDraft,
    rangeError,
    todayIso,
    onModeChange,
    onMonthChange,
    onRangeDraftChange,
    onApplyRange,
}: PeriodHeaderModel) {
    const showRange = canChooseRange && mode === 'range';
    const describedBy = rangeError ? RANGE_ERROR_ID : undefined;
    const maxDate = useMemo(() => pickerDateFromIsoDate(todayIso), [todayIso]);
    return (
        <div className="flex flex-wrap items-end gap-4">
            {canChooseRange && (
                <ToggleGroup options={MODE_OPTIONS} value={mode} onChange={onModeChange} ariaLabel="Statement period type" />
            )}
            {!showRange && (
                <div className="w-64">
                    <Label id={MONTH_LABEL_ID}>Statement month</Label>
                    <Select options={monthOptions} value={month} onChange={onMonthChange} ariaLabelledBy={MONTH_LABEL_ID} />
                </div>
            )}
            {showRange && (
                <>
                    <div>
                        <Label htmlFor={FROM_ID}>From</Label>
                        <DatePicker
                            id={FROM_ID}
                            value={rangeDraft.from ? pickerDateFromIsoDate(rangeDraft.from) : null}
                            maxDate={maxDate}
                            aria-describedby={describedBy}
                            aria-invalid={rangeError ? true : undefined}
                            onChange={(date) => onRangeDraftChange({ ...rangeDraft, from: date ? isoDateFromPickerDate(date) : null })}
                        />
                    </div>
                    <div>
                        <Label htmlFor={TO_ID}>To</Label>
                        <DatePicker
                            id={TO_ID}
                            value={rangeDraft.to ? pickerDateFromIsoDate(rangeDraft.to) : null}
                            maxDate={maxDate}
                            aria-describedby={describedBy}
                            aria-invalid={rangeError ? true : undefined}
                            onChange={(date) => onRangeDraftChange({ ...rangeDraft, to: date ? isoDateFromPickerDate(date) : null })}
                        />
                    </div>
                    <Button
                        type="button"
                        variant="primary"
                        appearance="solid"
                        ariaDisabled={rangeError !== null}
                        ariaDescribedBy={describedBy}
                        onClick={onApplyRange}
                    >
                        Apply
                    </Button>
                    {rangeError && (
                        <p id={RANGE_ERROR_ID} className="basis-full text-sm text-mp-muted">{rangeError}</p>
                    )}
                </>
            )}
        </div>
    );
}
